/**
 * PROC-000054 item 1 (revised - supersedes the numeric-only quantity fix):
 * quantity is a COMPOSITE value - a number plus a unit from uom_master
 * ("3 Month", "1200 Sqft"), not a bare number. Stored as the canonical
 * string "{number} {MasterUomName}" (leading number + exact master unit
 * name) - deterministic to parse back, and natural for the refinement
 * agent to read as free text.
 *
 * ONE parser (parseQuantityText) is shared by two seeding paths, both in
 * StepAnswers.jsx:
 *   (c) PREFILL - when the quantity answer is empty, seed it from
 *       parsed_requirement.quantity ("3 months" -> confirm, don't re-ask).
 *   (d) LEGACY/PROSE normalization - when a stored answer predates the
 *       composite input (a bare number from the prior numeric-only fix, or
 *       full prose from before that), normalize it into canonical form.
 * Both are deterministic, no LLM.
 *
 * The composite <input>+<Select> (SmartAnswerInput.jsx) never re-runs the
 * fuzzy parser on its own `value` prop - it only strictly splits/composes
 * already-canonical strings (splitQuantityComposite/formatQuantityComposite)
 * so a unit already resolved by one of the two effects above is never lost
 * to a transient re-parse before uom_master has loaded in the input itself.
 */

const normalize = (s) => String(s || '').toLowerCase();

// Multi-word aliases collapsed to a single token BEFORE matching, so
// "sq ft" / "sq. ft" match the same as "sqft" against uom_master's "Sqft".
// Symmetric "sq m" -> "sqm" for "Sqm" - same master-data class. Exported
// (MIXED-SEMANTICS + ECHO FIX BATCH, item F trace): statedIntentValidation
// Rules.js's matchStatedUnitAgainstMaster lacked this normalization
// entirely, so an echo_metadata unit of "sq ft" (space, as the LLM may
// preserve the user's own wording) never matched master's "Sqft" - the
// stated-unit default silently landed on no unit at all for the tile
// scenario ("1200 sq ft"), even though the number itself prefilled fine.
export const collapseMultiWordAliases = (text) =>
  normalize(text)
    .replace(/\bsq\.?\s*ft\b/g, 'sqft')
    .replace(/\bsq\.?\s*m\b/g, 'sqm');

/** First positive number token in free text, or '' if none. */
const firstNumberToken = (text) => {
  const match = String(text || '').match(/\d+(?:\.\d+)?/);
  return match ? match[0] : '';
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// uom_master rows sorted longest-name-first - so a multi-word/hyphenated
// name (e.g. "Man-Day") is always tried, and can match, before a SHORTER
// name that happens to appear as a substring inside it (e.g. "Day") ever
// gets a chance to.
const namesLongestFirst = (uomMaster) =>
  (uomMaster || [])
    .filter((u) => u.uom_name)
    .slice()
    .sort((a, b) => b.uom_name.length - a.uom_name.length);

/**
 * Match a unit against uom_master's uom_name list (case/plural tolerant).
 * Bug fix: the previous implementation tokenized text into bare `[a-z]+`
 * word fragments before comparing, which silently broke on any
 * hyphenated or multi-word master name - "Man-Day" tokenized to
 * ["man","day"], and "day" then matched uom_master's separate "Day"
 * entry, so selecting "Man-Day" in the composite input "became" Day on
 * the very next store/re-parse round trip (same for "Man-Month" ->
 * Month, and "Cubic Metre" -> Metre; "Sq Yard"/"Metric Tonne" simply
 * never matched anything, the same root cause's other failure mode).
 *
 * Fixed precedence, no bare substring matching anywhere:
 *   1. EXACT full-name match (case/plural tolerant) of whatever remains
 *      after stripping a single leading number - this is exactly what
 *      the canonical stored composite ("{number} {ExactMasterName}")
 *      always produces, so a round trip never even reaches word-boundary
 *      scanning below.
 *   2. Word-boundary substring match (`\bname s?\b`, never a bare
 *      `indexOf`/word-fragment compare) against uom_master sorted
 *      LONGEST-name-first, for free text that doesn't cleanly reduce to
 *      "{number} {unit}" (legacy prose, a stated requirement sentence).
 *      A shorter name can never match inside a longer one this way -
 *      "Man-Day" is tried, and matches, before "Day" is ever tested.
 */
const matchUnitWord = (text, uomMaster) => {
  if (!Array.isArray(uomMaster) || uomMaster.length === 0) return null;
  const collapsed = collapseMultiWordAliases(text);
  const remainder = collapsed.replace(/^\s*\d+(?:\.\d+)?\s*/, '').trim();

  if (remainder) {
    const exact = uomMaster.find((u) => {
      const name = normalize(u.uom_name);
      return name && (remainder === name || remainder === `${name}s`);
    });
    if (exact) return exact.uom_name;
  }

  for (const u of namesLongestFirst(uomMaster)) {
    const name = normalize(u.uom_name);
    const re = new RegExp(`\\b${escapeRegExp(name)}s?\\b`);
    if (re.test(collapsed)) return u.uom_name;
  }
  return null;
};

/** Deterministic {value, uom} extraction from free text - see module doc
 * for the two callers. `value` is the first positive number found as a
 * string ('' if none); `uom` is the matched uom_master uom_name, or null.
 * Never throws. */
export const parseQuantityText = (text, uomMaster = []) => ({
  value: firstNumberToken(text),
  uom: matchUnitWord(text, uomMaster),
});

/** Canonical stored form: "{number} {UnitName}", or just "{number}" when no
 * unit is set yet. '' in (no number) -> '' out, regardless of unit - a
 * unit alone isn't a quantity. */
export const formatQuantityComposite = (numberPart, unitPart) => {
  const num = String(numberPart ?? '').trim();
  const unit = String(unitPart ?? '').trim();
  if (!num) return '';
  return unit ? `${num} ${unit}` : num;
};

/** Strict split of an already-canonical composite string ("3 Month") into
 * {numberPart, unitPart} - used by the composite input to derive its two
 * sub-fields' displayed values, and by submit-time validation. uom_master
 * unit names are single words today, so splitting on the first space is
 * sufficient; any remainder is re-joined with a space as a defensive
 * no-op if that ever changes. */
export const splitQuantityComposite = (composite) => {
  const trimmed = String(composite || '').trim();
  if (!trimmed) return { numberPart: '', unitPart: '' };
  const [numberPart, ...rest] = trimmed.split(/\s+/);
  return { numberPart, unitPart: rest.join(' ') };
};

/** uom_master rows applicable to a procurement type's quantity dropdown -
 * 'goods'/'services' show applies_to === that type OR 'both'; 'mixed' (and
 * anything else) shows everything, since the headline quantity may
 * describe either component. */
export const filterUomsForType = (uomMaster, procurementType) => {
  if (!Array.isArray(uomMaster)) return [];
  if (procurementType !== 'goods' && procurementType !== 'services') return uomMaster;
  return uomMaster.filter((u) => u.applies_to === procurementType || u.applies_to === 'both');
};

/** Default unit for a fresh (no unit chosen/seeded yet) quantity composite -
 * "Each" for goods, per the ticket. No default for services/mixed - an
 * arbitrary default there would misrepresent the answer, unlike goods
 * where "Each" is genuinely the common case. Returns null when "Each"
 * isn't present in the given (already type-filtered) list. */
export const defaultUomForType = (procurementType, filteredUoms) => {
  if (procurementType !== 'goods') return null;
  const each = (filteredUoms || []).find((u) => normalize(u.uom_name) === 'each');
  return each ? each.uom_name : null;
};

/**
 * Item B (duration/dates cooperation, services only): the fixed set of
 * "calendar duration" units - a quantity in one of these describes a SPAN
 * of the engagement's own start/end dates, unlike an effort/volume unit
 * (Man-Day, Hour, Session, Engagement...) which describes work performed
 * WITHIN that span and has no fixed relationship to its length. Exact
 * master uom_name casing (data/uom_master.csv).
 */
export const CALENDAR_DURATION_UNITS = ['Month', 'Week'];

const DAYS_PER_CALENDAR_UNIT = { Month: 30, Week: 7 };

/** Whole-unit count between two ISO dates for a calendar-duration unit,
 * rounded to the nearest whole number (30-day months, 7-day weeks - simple
 * and deterministic, no calendar-aware month-length math). null when the
 * unit isn't a calendar-duration unit, either date is missing/invalid, or
 * the span isn't positive (end on/before start - a hard-validation concern
 * elsewhere, not this function's job). Never throws. */
export const deriveCalendarQuantityFromDates = (startIso, endIso, unitName) => {
  if (!CALENDAR_DURATION_UNITS.includes(unitName)) return null;
  if (!startIso || !endIso) return null;
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const days = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  if (days <= 0) return null;
  const rounded = Math.round(days / DAYS_PER_CALENDAR_UNIT[unitName]);
  return rounded > 0 ? rounded : null;
};

/**
 * Item E (display-only unit ranking): a small static map from uom_master
 * unit NAME to its physical/commercial DIMENSION - deliberately coarse-
 * grained (not a rigorous unit-of-measure ontology), just enough to tell
 * "1200 sq ft" (area) apart from "Kg" (weight) for ranking a "same
 * dimension as the stated unit" tier in the composite's unit dropdown.
 * Covers both data/uom_master.csv (the smaller, active list) and
 * uom_master_full.csv (a richer real-world master) - an unmapped name is
 * simply not ranked (dimension unknown, never guessed).
 *
 * SARU REDESIGN (2026-07-27 ruling): SARU (statedIntentValidationRules.js's
 * checkStatedUnitDimensionEcho) no longer uses this map - it reads the
 * uom_master row's own measurement_family/unit_semantics columns instead,
 * a finer-grained, data-driven model (this map's single 'count' dimension
 * conflated Consultant/License with Each/Unit, which the new schema
 * correctly tells apart as procurement_object vs generic_count). This map
 * survives ONLY for the ranking convenience below.
 */
export const UOM_DIMENSION_GROUPS = {
  // Area
  Sqft: 'area', Sqm: 'area', 'Sq Yard': 'area',
  // Length
  Metre: 'length', Centimetre: 'length', Millimetre: 'length', Foot: 'length', Inch: 'length', Kilometre: 'length',
  // Weight
  Kg: 'weight', Gram: 'weight', 'Metric Tonne': 'weight', Quintal: 'weight',
  // Volume
  Litre: 'volume', Millilitre: 'volume', 'Cubic Metre': 'volume',
  // Calendar time / duration
  Hour: 'time', Day: 'time', Week: 'time', Month: 'time', Year: 'time', Shift: 'time',
  // Labor effort (a count of effort-units, not a calendar span - "5
  // Man-Day" is not the same claim as "5 Day")
  'Man-Day': 'effort', 'Man-Month': 'effort',
  // Discrete count - physical items or service instances
  Each: 'count', Unit: 'count', Set: 'count', Lot: 'count', Pair: 'count', Dozen: 'count',
  Pack: 'count', Box: 'count', Carton: 'count', Ream: 'count', Roll: 'count',
  Engagement: 'count', Visit: 'count', Session: 'count', Trip: 'count', Consultant: 'count',
  License: 'count', User: 'count', Seat: 'count', Instance: 'count',
  // Digital storage
  GB: 'data', TB: 'data',
  // Energy
  KWh: 'energy',
};

/** The dimension group for a uom_master unit NAME, or null if unmapped -
 * an unmapped unit is never compared/ranked, not assumed to match or
 * differ from anything. */
export const unitDimension = (uomName) => UOM_DIMENSION_GROUPS[uomName] || null;

/**
 * Item E (display only - rank, never restrict, same doctrine as
 * CategorySelectCell's category tiers): given the full (already type-
 * filtered) uom list and the stated unit's own exact-cased master name (or
 * null), returns { suggested, all } - `suggested` is the stated unit
 * itself first, then any other units sharing its dimension; `all` is the
 * COMPLETE list, unchanged, always rendered below (SARU is the guard
 * against a cross-dimension pick, not this ranking - every unit stays
 * selectable from either tier). No stated unit, or a stated unit whose
 * dimension isn't in the map, returns an empty `suggested` tier.
 */
export const rankUomsForSuggestion = (uoms, statedUnitName) => {
  const list = Array.isArray(uoms) ? uoms : [];
  if (!statedUnitName) return { suggested: [], all: list };
  const statedDim = unitDimension(statedUnitName);
  const statedEntry = list.find((u) => u.uom_name === statedUnitName);
  const sameDimension = list.filter(
    (u) => u.uom_name !== statedUnitName && statedDim && unitDimension(u.uom_name) === statedDim
  );
  const suggested = statedEntry ? [statedEntry, ...sameDimension] : sameDimension;
  return { suggested, all: list };
};
